<script setup lang="ts">
import { ChevronDown, CircleStop, Pencil, Trash2 } from 'lucide-vue-next'

import useNotification from '@/composables/notification'

import BaseSupportConnection from '@/components/base/BaseSupportConnection.vue'
import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'

import useAudience from '@/composables/audiences'
import useOffer from '@/composables/offer'
import useReward from '@/composables/reward'
import { useOfferForm } from '@/composables/useOfferForm'
import { useOfferSummary } from '@/composables/useOfferSummary'

import OfferSummaryAside from './components/OfferSummaryAside.vue'
import OfferPromotionalCodeSettings from './components/OfferPromotionalCodeSettings.vue'

import type { Component } from 'vue'

import { getApiErrorMessage } from '@/utils/apiError.js'
import type { TabKey } from '@/components/base/BaseTab.vue'
import { useContentScopeSave } from '@/composables/contentScope'
import type { ContentScope } from '@/types/contentScope'
import { Card, CardHeader, CardTitle, CardContent } from '@/ui/card'
import BaseIcon from '@/components/base/BaseIcon.vue'

interface OfferAction {
  id: number
  text: string
  icon: Component
  variant?: 'destructive'
}

const ACTION_EDIT = 1
const ACTION_END = 2
const ACTION_DELETE = 3

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const notification = useNotification()
const { fetchOffer, deleteOffer, endOffer, updateOffer } = useOffer()
const { fetchMerchantAudiences } = useAudience()
const { fetchAllRewards } = useReward()

const isEditMode = computed(() => true)

const saveScope = useContentScopeSave()

const updateScope = async (value: ContentScope) => {
  const id = offerId.value
  if (id == null) return
  scope.value = await saveScope(async () => (await updateOffer(id, { scope: value })).scope ?? value)
}

const offerId = computed(() => {
  const raw = route.params.offerId
  if (!raw) return null
  const value = Array.isArray(raw) ? Number(raw[0]) : Number(raw)
  return Number.isNaN(value) ? null : value
})

const {
  scope,
  creator,
  offerName,
  rewards,
  allCustomerLists,
  targetAudience,
  selectedSegmentIds,
  scheduleStartDate,
  scheduleEndDate,
  timeSlots,
  selectedChannels,
  offerStoreIds,
  claimLimitQuantity,
  claimLimitDays,
  isPromotionalCodeRequired,
  selectedReward,
  hydrateFromOffer
} = useOfferForm({ isEditMode })

const { offerAudienceSummary, offerScheduleSummary, offerStatus, offerChannelsSummary, offerStoresSummary } =
  useOfferSummary({
    targetAudience,
    selectedSegmentIds,
    allCustomerLists,
    scheduleStartDate,
    scheduleEndDate,
    timeSlots,
    selectedChannels,
    offerStoreIds
  })

const isLoaded = ref(false)
const isDeleteModalOpen = ref(false)
const currentTab = ref((route.query?.activeTab as string) || '')

const tabs = computed<TabKey[]>(() => [
  { value: 'performance', name: t('pages.offer.detail.tabs.performance') },
  ...(isPromotionalCodeRequired.value ? [{ value: 'codes', name: t('pages.offer.detail.tabs.codes') }] : [])
])

const actionsTitle = computed(() => t('pages.offer.detail.buttons.title'))

const actionsValues = computed((): OfferAction[] => {
  const actions: OfferAction[] = [
    {
      id: ACTION_EDIT,
      text: t('pages.offer.detail.buttons.edit'),
      icon: Pencil
    }
  ]

  if (offerStatus.value === 'active') {
    actions.push({
      id: ACTION_END,
      text: t('pages.offer.detail.buttons.end'),
      icon: CircleStop
    })
  }

  actions.push({
    id: ACTION_DELETE,
    text: t('pages.offer.detail.buttons.delete'),
    icon: Trash2,
    variant: 'destructive'
  })

  return actions
})

const fetchRewards = async () => {
  try {
    const data = await fetchAllRewards()
    rewards.value = data || []
  } catch {
    rewards.value = []
  }
}

const fetchAllCustomerLists = async () => {
  try {
    const response = await fetchMerchantAudiences({
      pagination: { pageSize: 1000, page: 1 }
    })
    allCustomerLists.value = response?.data ?? []
  } catch {
    allCustomerLists.value = []
  }
}

const loadOffer = async () => {
  if (offerId.value == null) return

  const data = await fetchOffer(offerId.value)
  hydrateFromOffer(data)

  if (data.reward && !rewards.value.some((reward) => reward.id === data.reward?.id)) {
    rewards.value = [...rewards.value, data.reward]
  }

  if (!currentTab.value) {
    currentTab.value = data.isPromotionalCodeRequired ? 'codes' : 'performance'
  }
}

const selectAction = async (actionId: number) => {
  if (actionId === ACTION_EDIT) {
    router.push({ name: 'OfferEdit', params: { offerId: offerId.value } })
    return
  }

  if (actionId === ACTION_END) {
    await handleEndOffer()
    return
  }

  if (actionId === ACTION_DELETE) {
    isDeleteModalOpen.value = true
  }
}

const handleEndOffer = async () => {
  if (offerId.value == null) return

  try {
    await endOffer(offerId.value)
    notification.create('success', { text: t('pages.offer.list.endSuccess') })
    await loadOffer()
  } catch (error: unknown) {
    notification.create('error', {
      text: getApiErrorMessage(error) ?? t('pages.offer.list.endError')
    })
  }
}

const handleDeleteOffer = async () => {
  if (offerId.value == null) return

  try {
    await deleteOffer(offerId.value)
    notification.create('success', { text: t('pages.offer.list.deleteSuccess') })
    router.push({ name: 'OfferList' })
  } catch (error: unknown) {
    notification.create('error', {
      text: getApiErrorMessage(error) ?? t('pages.offer.list.deleteError')
    })
  } finally {
    isDeleteModalOpen.value = false
  }
}

onMounted(async () => {
  if (offerId.value == null) {
    router.push({ name: 'OfferList' })
    return
  }

  try {
    await Promise.all([fetchRewards(), fetchAllCustomerLists(), loadOffer()])
  } catch {
    notification.create('error', { text: t('pages.offer.list.fetchError') })
    router.push({ name: 'OfferList' })
    return
  }

  isLoaded.value = true
})
</script>

<template>
  <TheLoadingPage v-if="!isLoaded" />

  <div v-else>
    <div class="bg-white">
      <TheDetailHeader
        v-if="offerName"
        :title="offerName"
        :parent="{ label: t('pages.offer.create.crumbs.promotions'), to: { name: 'OfferList' } }"
      >
        <template #badge>
          <span class="flex items-center gap-2">
            <ContentScopeDetailBadge :scope subject="offer" :save="updateScope" />
            <ContentScopeCreator :creator="creator" />
          </span>
        </template>
        <template #actions>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="outline" class="w-full md:w-[190px]">
                {{ actionsTitle }}
                <ChevronDown class="ml-auto" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-[190px]">
              <DropdownMenuItem
                v-for="action in actionsValues"
                :key="action.id"
                :variant="action.variant"
                @click="selectAction(action.id)"
              >
                <component :is="action.icon" />
                {{ action.text }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </template>
      </TheDetailHeader>

      <BaseTab v-if="isPromotionalCodeRequired" v-model="currentTab" :tabs="tabs" is-full-width />
    </div>

    <div class="px-6 pb-6 sm:px-12 sm:pb-12 flex flex-row gap-10" :class="isPromotionalCodeRequired && 'pt-6 md:pt-8'">
      <div v-if="currentTab === 'codes'" class="flex-1 min-w-0">
        <OfferPromotionalCodeSettings
          v-if="offerId != null"
          :offer-id="offerId"
          :is-promotional-code-required="isPromotionalCodeRequired"
        />
      </div>
      <div v-if="currentTab === 'performance'" class="flex-1 flex flex-col gap-5">
        <Card>
          <CardHeader>
            <CardTitle>{{ t('pages.offer.detail.performance.title') }}</CardTitle>
            <CardDescription>{{ t('pages.offer.detail.performance.description') }}</CardDescription>
          </CardHeader>
          <CardContent>
            <div class="flex min-h-48 flex-col items-center justify-center rounded-lg bg-muted p-4 gap-4">
              <BaseIcon name="IconoirActivity" class="h-12 w-12 text-muted-foreground" />
              <p class="text-sm text-muted-foreground">
                {{ t('pages.offer.detail.performance.notYetAvailable') }}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div class="lg:sticky lg:top-24 lg:self-start max-w-[450px]">
        <OfferSummaryAside
          :offer-name="offerName"
          :selected-reward="selectedReward"
          :claim-limit-quantity="claimLimitQuantity"
          :claim-limit-days="claimLimitDays"
          :audience-summary="offerAudienceSummary"
          :schedule-summary="offerScheduleSummary"
          :channels-summary="offerChannelsSummary"
          :stores-summary="offerStoresSummary"
          :is-promotional-code-required="isPromotionalCodeRequired"
          :status="offerStatus"
        />
      </div>
    </div>
  </div>

  <BaseModal :is-open="isDeleteModalOpen" size="lg" :show-footer="false" @close="isDeleteModalOpen = false">
    <div class="flex w-full flex-row justify-center">
      <div class="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100">
        <Trash2 class="h-6 w-6 text-rose-600" />
      </div>
    </div>
    <p class="mt-5 text-center font-semibold">
      {{ t('pages.offer.list.deleteModal.title') }}
    </p>
    <p class="mt-2 text-center text-sm text-foreground-secondary">
      {{ t('pages.offer.list.deleteModal.body', { name: offerName }) }}
    </p>
    <div class="my-4 flex justify-center">
      <BaseSupportConnection />
    </div>
    <div class="flex w-full flex-row justify-center gap-3">
      <Button variant="outline" class="flex-1" @click="isDeleteModalOpen = false">
        {{ t('global.words.cancel') }}
      </Button>
      <Button variant="destructive" class="flex-1" @click="handleDeleteOffer">
        {{ t('pages.offer.list.actions.delete') }}
      </Button>
    </div>
  </BaseModal>
</template>
